import { z } from "zod";
import { createOllamaGenerator } from "@fathom/core";
import { ExtractedItem } from "./types.js";

// Lazy-load the Anthropic SDK to avoid crashing when it's not installed
type Anthropic = import("@anthropic-ai/sdk").default;
async function getAnthropicClient(apiKey?: string): Promise<Anthropic> {
  const { default: AnthropicSDK } = await import("@anthropic-ai/sdk");
  return new AnthropicSDK({ apiKey: apiKey ?? process.env.ANTHROPIC_API_KEY });
}

export const EXTRACTION_SYSTEM_PROMPT = `You are a technical project analyst. Your job is to extract discrete, actionable work items from raw input (user feedback, bug reports, meeting notes, design handoffs, etc.).

For each work item, provide:
- name: Short descriptive title (3-8 words)
- category: One of: "Infrastructure", "Frontend", "Backend", "Performance", "Bug Fix", "Integration", "DevOps", "Documentation", "Refactoring", "Testing"
- complexity: One of: "S" (single file, simple change), "M" (2-5 files, typical feature), "L" (5-15 files, multiple integrations), "XL" (15+ files, system-wide)
- priority: One of: "P0" (blocker), "P1" (must-have), "P2" (should-have), "P3" (nice-to-have)
- description: 2-3 sentences explaining the work, including likely technical approach
- rootCause: Optional — for bugs, explain the likely root cause
- aiTasks: Array of 2-4 discrete AI coding tasks (e.g., "scaffold-auth", "fix-email-template", "write-perf-tests")
- acceptanceCriteria: One sentence describing how to verify the work is done

Split compound requests into separate items. Identify implicit requirements. Be specific about technical approach.

Respond with ONLY a JSON array of items. No markdown, no explanation.`;

const ExtractionResponse = z.array(ExtractedItem);

/**
 * A generate function that any LLM provider can implement.
 * Takes a system prompt and user prompt, returns the text response.
 */
export type GenerateFunction = (systemPrompt: string, userPrompt: string) => Promise<string>;

export interface ExtractorOptions {
  /** Anthropic API key (for built-in Anthropic provider) */
  apiKey?: string;
  /** Model ID (for built-in providers) */
  model?: string;
  /** Pre-configured Anthropic client */
  client?: Anthropic;
  /** LLM provider: 'anthropic' (default), 'openai', 'ollama', or 'custom' */
  provider?: 'anthropic' | 'openai' | 'ollama' | 'custom';
  /** Bring-your-own generate function (overrides provider) */
  generate?: GenerateFunction;
  /** OpenAI API key (for openai provider) */
  openaiApiKey?: string;
  /** Ollama base URL (defaults to http://localhost:11434) */
  ollamaUrl?: string;
  /** Ollama model name (defaults to qwen2.5-coder) */
  ollamaModel?: string;
}

/**
 * Create a generate function for the Anthropic provider.
 */
function createAnthropicGenerator(options: ExtractorOptions): GenerateFunction {
  return async (systemPrompt: string, userPrompt: string): Promise<string> => {
    const client = options.client ?? await getAnthropicClient(options.apiKey);
    const model = options.model ?? "claude-sonnet-4-20250514";

    const response = await client.messages.create({
      model,
      max_tokens: 4096,
      system: systemPrompt,
      messages: [{ role: "user", content: userPrompt }],
    });

    const textBlock = response.content.find((block) => block.type === "text");
    if (!textBlock || textBlock.type !== "text") {
      throw new Error("No text content in Anthropic response");
    }
    return textBlock.text;
  };
}

/**
 * Create a generate function for the OpenAI provider.
 * Dynamically imports the openai package.
 */
function createOpenAIGenerator(options: ExtractorOptions): GenerateFunction {
  return async (systemPrompt: string, userPrompt: string): Promise<string> => {
    const { default: OpenAI } = await import("openai");
    const client = new OpenAI({
      apiKey: options.openaiApiKey ?? process.env.OPENAI_API_KEY,
    });
    const model = options.model ?? "gpt-4o";

    const response = await client.chat.completions.create({
      model,
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
      max_tokens: 4096,
    });

    const text = response.choices[0]?.message?.content;
    if (!text) {
      throw new Error("No text content in OpenAI response");
    }
    return text;
  };
}

/**
 * Resolve the generate function from options.
 * Priority: custom generate > provider-specific > default (anthropic)
 */
function resolveGenerator(options: ExtractorOptions): GenerateFunction {
  if (options.generate) {
    return options.generate;
  }

  const provider = options.provider ?? 'anthropic';

  switch (provider) {
    case 'openai':
      return createOpenAIGenerator(options);
    case 'ollama':
      return createOllamaGenerator({
        url: options.ollamaUrl,
        model: options.ollamaModel ?? options.model,
      });
    case 'anthropic':
    default:
      return createAnthropicGenerator(options);
  }
}

/**
 * Parse LLM response text into extracted items.
 * Handles markdown code block wrapping, JSON parsing, and Zod validation.
 */
export function parseExtractionResponse(text: string): ExtractedItem[] {
  let jsonText = text.trim();
  if (jsonText.startsWith("```")) {
    jsonText = jsonText.replace(/^```(?:json)?\n?/, "").replace(/\n?```$/, "");
  }

  const parsed = JSON.parse(jsonText);
  return ExtractionResponse.parse(parsed);
}

/**
 * Create a reusable extractor with pre-configured options.
 * This is the recommended way to use the intake pipeline with any provider.
 */
export function createExtractor(options: ExtractorOptions = {}) {
  const generate = resolveGenerator(options);

  return {
    /**
     * Extract discrete work items from raw unstructured text.
     */
    async extract(rawInput: string): Promise<ExtractedItem[]> {
      const userPrompt = `Extract work items from the following input:\n\n${rawInput}`;
      const text = await generate(EXTRACTION_SYSTEM_PROMPT, userPrompt);
      return parseExtractionResponse(text);
    },

    /**
     * Extract with a custom system prompt (e.g., intent-informed).
     */
    async extractWithPrompt(rawInput: string, systemPrompt: string): Promise<ExtractedItem[]> {
      const userPrompt = `Extract work items from the following input:\n\n${rawInput}`;
      const text = await generate(systemPrompt, userPrompt);
      return parseExtractionResponse(text);
    },
  };
}

/**
 * Extract discrete work items from raw unstructured text using any LLM provider.
 * Backwards-compatible: defaults to Anthropic Claude if no provider specified.
 */
export async function extractWorkItems(
  rawInput: string,
  options: ExtractorOptions = {},
): Promise<ExtractedItem[]> {
  const extractor = createExtractor(options);
  return extractor.extract(rawInput);
}
